import { formatIntegrationsCell } from '../constants/integrationLabels';
import { getRiskLevelShortLabel, getRiskShortLabel } from '../constants/riskTypes';

const CONNECTION_SCHEDULE = {
  daily: { text: 'каждый день', title: 'Выход на связь каждый день' },
  weekly: { text: 'раз в неделю', title: 'Выход на связь раз в неделю' },
  monthly: { text: 'раз в месяц', title: 'Выход на связь раз в месяц' },
};

function formatScheduleCell(hit) {
  const facts = hit?.facts || {};
  const features = hit?.features || {};
  const riskNumber = Number(hit?.risk_number);

  if (riskNumber === 2) {
    const day = Number(features.send_at_day ?? facts.send_at_day);
    if (Number.isFinite(day) && day > 0) {
      return {
        text: `${day}-е число`,
        title: `Передача показаний ${day}-го числа каждого месяца (MSK)`,
      };
    }

    const raw = String(features.transmission_schedule_label || '');
    if (raw) {
      return {
        text: raw.replace(' месяца (MSK)', '').replace(' (MSK)', ''),
        title: `День передачи из настройки: ${raw}`,
      };
    }

    return { text: '—', title: 'День передачи не задан в настройке интеграции' };
  }

  if (riskNumber === 3 || riskNumber === 4) {
    const hours = Number(features.period_seconds) / 3600;
    if (Number.isFinite(hours) && hours > 0) {
      const text = hours >= 24 ? `${Number((hours / 24).toFixed(1))} дн.` : `${Math.round(hours)} ч.`;
      return { text, title: `Номинальный период выхода контроллера: ${text}` };
    }
    return { text: '—', title: 'Период выхода не определён' };
  }

  const frequency = features.schedule_frequency;
  if (frequency === 'monthly') {
    const dayMatch = String(features.schedule_detail_label || '').match(/(\d+)-е/);
    if (dayMatch) {
      const day = dayMatch[1];
      return {
        text: `${day}-е число`,
        title: `Выход на связь ${day}-го числа каждого месяца`,
      };
    }
  }

  return CONNECTION_SCHEDULE[frequency] || { text: '—', title: '' };
}

export function buildCasePath(header, organizationLabel) {
  return [
    organizationLabel || (header.organization_id != null ? `УК #${header.organization_id}` : null),
    header.user_full_name || (header.user_id != null ? `User #${header.user_id}` : null),
    header.address || null,
    header.controller_serial || null,
  ]
    .filter(Boolean)
    .join(' › ');
}

export function pickMaxHit(hits = []) {
  if (hits.length === 0) {
    return null;
  }

  return hits.reduce((best, hit) => {
    const score = hit.rule_result?.risk_score;
    const bestScore = best.rule_result?.risk_score;
    if (score == null && bestScore == null) return best;
    if (score == null) return best;
    if (bestScore == null) return hit;
    return score > bestScore ? hit : best;
  }, hits[0]);
}

/**
 * Collapsed root summary: prefer the risk(s) selected in the dashboard filter.
 * Among preferred hits pick max score; if none match, fall back to all hits.
 */
export function pickSummaryHit(hits = [], preferredRiskNumbers = null) {
  const list = Array.isArray(hits) ? hits : [];
  if (!list.length) {
    return null;
  }
  const preferred = (preferredRiskNumbers || [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));
  if (!preferred.length) {
    return pickMaxHit(list);
  }
  const pool = list.filter((hit) => preferred.includes(Number(hit.risk_number)));
  return pickMaxHit(pool.length ? pool : list);
}

function hitSerialNumber(hit) {
  const facts = hit?.facts || {};
  const riskNumber = Number(hit?.risk_number);
  // Canon: Risk #2 is per counter — show counter serial so sibling #2 rows differ.
  if (riskNumber === 2) {
    return (
      facts.counter_serial_number ||
      facts.serial_number ||
      facts.controller_serial_number ||
      '—'
    );
  }
  return facts.serial_number || facts.controller_serial_number || '—';
}

export function buildHitRowLabel(hit) {
  if (!hit) {
    return '—';
  }

  const base = `#${hit.risk_number ?? '?'} ${getRiskShortLabel(Number(hit.risk_number))}`;
  if (Number(hit.risk_number) !== 2) {
    return base;
  }
  const counterSerial = hit.facts?.counter_serial_number;
  return counterSerial ? `${base} · ${counterSerial}` : base;
}

export function buildHitRowCells(hit) {
  const facts = hit?.facts || {};
  const features = hit?.features || {};
  const rule = hit?.rule_result || {};
  const schedule = formatScheduleCell(hit);

  const integrations = formatIntegrationsCell(facts);

  const riskNumber = Number(hit?.risk_number);
  const missedTxRaw =
    features.missed_transmission_periods ?? features.missed_transmission_windows;
  const missedTx =
    riskNumber === 1 || riskNumber === 2
      ? String(Number.isFinite(Number(missedTxRaw)) ? Number(missedTxRaw) : 0)
      : '—';

  return {
    serial: hitSerialNumber(hit),
    connection: facts.controller_connection_type_label || facts.controller_connection_type || '—',
    firmware: facts.firmware_version || facts.controller_firmware_version || '—',
    schedule: schedule.text,
    scheduleTitle: schedule.title,
    integrations,
    hasComment: Boolean(facts.has_comment),
    riskLevel: getRiskLevelShortLabel(rule.risk_level),
    score: rule.risk_score != null ? Number(rule.risk_score).toFixed(2) : '—',
    overdue:
      riskNumber === 3 && features.last_signal_dbm != null
        ? `${features.last_signal_dbm} дБм`
        : features.days_overdue != null && features.days_overdue > 0
        ? `${Math.round(features.days_overdue)}д`
        : '—',
    missedTx,
    missedTxTitle: 'Пропущено передач (Instant из настройки интеграции)',
  };
}

export function buildCaseRowCells(riskCase, preferredRiskNumbers = null) {
  const summaryHit = pickSummaryHit(riskCase.hits, preferredRiskNumbers);
  const cells = buildHitRowCells(summaryHit);
  const riskNumber = summaryHit?.risk_number;
  return {
    path: buildCasePath(riskCase.header, null),
    ...cells,
    // Root row: which risk the summary metrics belong to (#N · level).
    riskLevel:
      riskNumber != null ? `#${riskNumber} · ${cells.riskLevel}` : cells.riskLevel,
    maxHit: summaryHit,
    summaryHit,
  };
}

/**
 * Expanded root row: path-only header (FIO / address / serial path).
 * Risk metrics move to the #1–#4 child rows below.
 */
export function buildCaseHeaderRowCells(fullPath) {
  return {
    path: fullPath || '—',
    serial: '—',
    connection: '—',
    firmware: '—',
    schedule: '—',
    scheduleTitle: '',
    integrations: '—',
    hasComment: false,
    riskLevel: '—',
    score: '—',
    overdue: '—',
    missedTx: '—',
    missedTxTitle: '',
  };
}
